/**
 * Orçamento do snapshot de tela, agora em TRÊS listas.
 *
 * O contrato publicado ao backend separa o que a tela É (`estrutura`), o que
 * ela MOSTRA (`dados`) e o que a pessoa pode FAZER ou PREENCHER nela
 * (`acoes_e_campos`). A separação não é cosmética: numa lista só, uma linha de
 * tabela e um campo de formulário chegavam ao modelo com a mesma cara, e ele
 * respondia sobre a tela como se tudo fosse rascunho de digitação.
 *
 * Cada lista tem teto próprio para que ruído de uma classe não monopolize o
 * envio, e todas dividem um teto de caracteres — é ele que segura o custo do
 * turno. Toda sobra de uma classe fica disponível para as seguintes.
 */

/** Teto de itens de ESTRUTURA (tela, modo, período, abas, filtros, colunas). */
export const LIMITE_DE_ITENS_DE_ESTRUTURA = 40

/** Teto de itens de DADOS (linhas, valores, totais). Um mês de folha cabe. */
export const LIMITE_DE_ITENS_DE_DADOS = 80

/** Teto de itens de AÇÕES E CAMPOS (botões, links, controles de formulário). */
export const LIMITE_DE_ITENS_DE_ACOES_E_CAMPOS = 60

/**
 * Teto do snapshot inteiro, somadas as três listas. Continua existindo para
 * que nenhuma combinação de tetos de classe estoure o contrato do backend.
 */
export const LIMITE_DE_ITENS_DO_SNAPSHOT =
  LIMITE_DE_ITENS_DE_ESTRUTURA + LIMITE_DE_ITENS_DE_DADOS + LIMITE_DE_ITENS_DE_ACOES_E_CAMPOS

/**
 * Teto do backend para cada rótulo e cada valor.
 *
 * 240 e não 120: uma linha de folha é `data · empregado · jornada · marcações
 * · saldo · status`, e cortar em 120 decapitava justamente as colunas finais —
 * as que dizem o que está pendente.
 */
export const LIMITE_DE_TEXTO_DO_ITEM = 240

/** Orçamento local, somando rótulos e valores de todos os itens. */
export const LIMITE_DE_CARACTERES_DO_SNAPSHOT = 12_000

export const LIMITE_DE_ESTADOS = 6
export const PISO_DE_CAMPOS_DE_FORMULARIO = 30
export const LIMITE_DE_CAMPOS_DE_FORMULARIO = 40
export const LIMITE_DE_ACOES = 20
export const LIMITE_DE_REGIOES = 4

/**
 * Linhas por região. 40 cobre o mês inteiro de uma folha por empregado; abaixo
 * disso o modelo recebia meia tela e concluía coisas sobre a metade que viu.
 */
export const LIMITE_DE_LINHAS_POR_REGIAO = 40

export interface ItemOrcamentavel {
  rotulo: string | null
  valor: string
  rotulo_truncado?: true
  valor_truncado?: true
}

export interface RegiaoComLinhas<T extends ItemOrcamentavel> {
  regiao: T
  linhas: T[]
}

export interface CandidatosAoSnapshot<T extends ItemOrcamentavel> {
  /** Identidade, modo, período, abas, filtros e contagens. */
  estrutura: T[]
  /** Controles de formulário lidos do DOM. */
  campos: T[]
  /** Botões, links e itens de menu acionáveis. */
  acoes: T[]
  /** Valores e totais que a própria tela publicou. */
  dados: T[]
  /** Tabelas e listas observadas, cada uma com as suas linhas. */
  regioes: Array<RegiaoComLinhas<T>>
}

export interface ResultadoDoOrcamento<T extends ItemOrcamentavel> {
  estrutura: T[]
  dados: T[]
  acoes_e_campos: T[]
  omitidosPorLimite: number
}

function limitarItem<T extends ItemOrcamentavel> (item: T): T {
  let limitado: ItemOrcamentavel = item

  if (item.rotulo !== null && item.rotulo.length > LIMITE_DE_TEXTO_DO_ITEM) {
    limitado = {
      ...limitado,
      rotulo: item.rotulo.slice(0, LIMITE_DE_TEXTO_DO_ITEM),
      rotulo_truncado: true
    }
  }

  if (item.valor.length > LIMITE_DE_TEXTO_DO_ITEM) {
    limitado = {
      ...limitado,
      valor: item.valor.slice(0, LIMITE_DE_TEXTO_DO_ITEM),
      valor_truncado: true
    }
  }

  return limitado as T
}

function custoDoItem (item: ItemOrcamentavel): number {
  return (item.rotulo?.length ?? 0) + item.valor.length
}

interface Lista<T> {
  itens: T[]
  teto: number
}

/**
 * Preenche as três listas na ordem de prioridade: primeiro o que identifica a
 * tela, depois o que a pessoa está preenchendo, depois o que ela pode acionar,
 * e por fim o corpo de dados — que é o maior e o mais fácil de recortar sem
 * deixar o modelo sem chão.
 *
 * O piso de campos é uma reserva de ORDEM: os primeiros 30 controles são
 * tentados antes das ações e dos dados; do 31º ao 40º disputam a sobra.
 */
export function preencherOrcamentoDoSnapshot<T extends ItemOrcamentavel> (
  candidatos: CandidatosAoSnapshot<T>
): ResultadoDoOrcamento<T> {
  const estrutura: Lista<T> = { itens: [], teto: LIMITE_DE_ITENS_DE_ESTRUTURA }
  const dados: Lista<T> = { itens: [], teto: LIMITE_DE_ITENS_DE_DADOS }
  const acoesECampos: Lista<T> = { itens: [], teto: LIMITE_DE_ITENS_DE_ACOES_E_CAMPOS }

  let caracteresUsados = 0
  let itensAceitos = 0
  let omitidosPorLimite = 0

  const tentarAdicionar = (lista: Lista<T>, candidato: T): boolean => {
    const item = limitarItem(candidato)
    const custo = custoDoItem(item)

    if (
      lista.itens.length >= lista.teto ||
      itensAceitos >= LIMITE_DE_ITENS_DO_SNAPSHOT ||
      caracteresUsados + custo > LIMITE_DE_CARACTERES_DO_SNAPSHOT
    ) {
      omitidosPorLimite += 1
      return false
    }

    lista.itens.push(item)
    caracteresUsados += custo
    itensAceitos += 1
    return true
  }

  for (const item of candidatos.estrutura.slice(0, LIMITE_DE_ITENS_DE_ESTRUTURA)) {
    tentarAdicionar(estrutura, item)
  }
  omitidosPorLimite += Math.max(0, candidatos.estrutura.length - LIMITE_DE_ITENS_DE_ESTRUTURA)

  const camposAteOPiso = candidatos.campos.slice(0, PISO_DE_CAMPOS_DE_FORMULARIO)
  const camposDaFaixaElastica = candidatos.campos.slice(
    PISO_DE_CAMPOS_DE_FORMULARIO,
    LIMITE_DE_CAMPOS_DE_FORMULARIO
  )
  for (const campo of camposAteOPiso) tentarAdicionar(acoesECampos, campo)
  omitidosPorLimite += Math.max(0, candidatos.campos.length - LIMITE_DE_CAMPOS_DE_FORMULARIO)

  for (const acao of candidatos.acoes.slice(0, LIMITE_DE_ACOES)) {
    tentarAdicionar(acoesECampos, acao)
  }
  omitidosPorLimite += Math.max(0, candidatos.acoes.length - LIMITE_DE_ACOES)

  for (const dado of candidatos.dados.slice(0, LIMITE_DE_ITENS_DE_DADOS)) {
    tentarAdicionar(dados, dado)
  }
  omitidosPorLimite += Math.max(0, candidatos.dados.length - LIMITE_DE_ITENS_DE_DADOS)

  const regioesAceitas: Array<RegiaoComLinhas<T>> = []
  for (const grupo of candidatos.regioes.slice(0, LIMITE_DE_REGIOES)) {
    if (tentarAdicionar(estrutura, grupo.regiao)) {
      regioesAceitas.push(grupo)
    } else {
      omitidosPorLimite += grupo.linhas.length
    }
  }

  for (const grupo of candidatos.regioes.slice(LIMITE_DE_REGIOES)) {
    omitidosPorLimite += 1 + grupo.linhas.length
  }

  for (const grupo of regioesAceitas) {
    for (const linha of grupo.linhas.slice(0, LIMITE_DE_LINHAS_POR_REGIAO)) {
      tentarAdicionar(dados, linha)
    }
    omitidosPorLimite += Math.max(0, grupo.linhas.length - LIMITE_DE_LINHAS_POR_REGIAO)
  }

  for (const campo of camposDaFaixaElastica) tentarAdicionar(acoesECampos, campo)

  return {
    estrutura: estrutura.itens,
    dados: dados.itens,
    acoes_e_campos: acoesECampos.itens,
    omitidosPorLimite
  }
}
